import { useState } from 'react';
import { PresetterMode } from '../utils/constants';
import { formatNumber } from '../utils/binaryConversion';
import { CalculationResult } from '../hooks/useOffsetCalculator';
import { BinaryOutputGrid } from './BinaryOutputGrid';

interface ResultDisplayProps {
  mode: PresetterMode;
  result: CalculationResult | null;
  onSaveToHistory: () => void;
}

export function ResultDisplay({ mode, result, onSaveToHistory }: ResultDisplayProps) {
  const [copied, setCopied] = useState(false);

  if (!result) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          New Offset Result
        </h2>
        <div className="p-8 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg">
          Enter test cut measurements to calculate the new offset
        </div>
      </div>
    );
  }

  const copyToClipboard = async () => {
    try {
      // Format as 4 bytes separated by spaces for easy entry
      const formattedBinary = result.newBytes.join(' ');
      await navigator.clipboard.writeText(formattedBinary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          New Offset Result
        </h2>
        <div className="flex gap-2">
          <button
            onClick={onSaveToHistory}
            className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-md transition-colors"
          >
            Save to History
          </button>
          <button
            onClick={copyToClipboard}
            className={`
              px-3 py-1.5 text-sm rounded-md transition-colors
              ${copied
                ? 'bg-green-500 text-white'
                : 'bg-industrial-500 hover:bg-industrial-600 text-white'
              }
            `}
          >
            {copied ? 'Copied!' : 'Copy Binary'}
          </button>
        </div>
      </div>

      {/* Decimal Result */}
      <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            New Offset (Decimal):
          </span>
          <span className="font-mono text-2xl font-bold text-green-700 dark:text-green-400">
            {formatNumber(result.newOffset)}
          </span>
        </div>
      </div>

      {/* Binary Output Grid */}
      <BinaryOutputGrid mode={mode} bytes={result.newBytes} />

      {/* Calculation Summary */}
      <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-gray-600 dark:text-gray-400">
          <div>
            <span className="font-medium">Original:</span>{' '}
            <span className="font-mono">{formatNumber(result.originalOffset)}</span>
          </div>
          <div>
            <span className="font-medium">Error × 1000:</span>{' '}
            <span className="font-mono">{result.error >= 0 ? '+' : ''}{(result.error * 1000).toFixed(0)}</span>
          </div>
          <div>
            <span className="font-medium">New:</span>{' '}
            <span className="font-mono">{formatNumber(result.newOffset)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
